/**
 * GraphQL Queries for SPLH Performance Dashboard V2
 *
 * V2 queries include comparison mode support for period-over-period analysis.
 * Kept separate from auto-generated src/graphql/queries.js to prevent overwrites.
 *
 * @module pages/common/dashboards/SPLHDashboard/services/splhQueriesV2
 */

/**
 * Query to fetch complete SPLH Dashboard data with comparison support (V2)
 *
 * Returns comparison-aware data structures with MetricValue objects containing:
 * - current: Current period value
 * - comparison: Comparison period value
 * - delta: Absolute change
 * - deltaPct: Percentage change
 * - direction: UP, DOWN, or FLAT
 *
 * @type {string}
 */
export const getSPLHDashboardDataV2 = /* GraphQL */ `
  query GetSPLHDashboardDataV2($input: SPLHDashboardInputV2!) {
    getSPLHDashboardDataV2(input: $input) {
      # Comparison context
      comparisonMode
      currentRange {
        startDate
        endDate
      }
      comparisonRange {
        startDate
        endDate
      }

      # KPI tiles with comparison data
      kpis {
        metricName
        value {
          current
          comparison
          delta
          deltaPct
          direction
        }
        subMetrics {
          foh
          boh
          fohComparison
          bohComparison
        }
      }

      # Trend data with dual series
      trendData {
        date
        currentSPLH
        comparisonSPLH
        deltaPct
        currentSales
        comparisonSales
        currentHours
        comparisonHours
      }

      # Hierarchy data with comparison metrics
      hierarchyData {
        id
        name
        level
        parentId
        splh {
          current
          comparison
          delta
          deltaPct
          direction
        }
        sales {
          current
          comparison
          delta
          deltaPct
          direction
        }
        laborHours {
          current
          comparison
          delta
          deltaPct
          direction
        }
        laborDollars {
          current
          comparison
          delta
          deltaPct
          direction
        }
        laborPct {
          current
          comparison
          delta
          deltaPct
          direction
        }
        checkCount {
          current
          comparison
          delta
          deltaPct
          direction
        }
        fohHours
        bohHours
        fohSPLH
        bohSPLH
        hasChildren
        children {
          id
          name
          level
          parentId
          splh {
            current
            comparison
            delta
            deltaPct
            direction
          }
          sales {
            current
            comparison
            delta
            deltaPct
            direction
          }
          laborHours {
            current
            comparison
            delta
            deltaPct
            direction
          }
          laborDollars {
            current
            comparison
            delta
            deltaPct
            direction
          }
          laborPct {
            current
            comparison
            delta
            deltaPct
            direction
          }
          checkCount {
            current
            comparison
            delta
            deltaPct
            direction
          }
          fohHours
          bohHours
          fohSPLH
          bohSPLH
          hasChildren
        }
      }

      # Breakdown data with comparison
      # Performer items now include all 6 key metrics matching HierarchyTableV2
      breakdownData {
        topPerformers {
          locationId
          locationName
          orgLevel_1_Id
          orgLevel_1_Name
          orgLevel_2_Id
          orgLevel_2_Name
          orgLevel_3_Id
          orgLevel_3_Name
          # 1. SPLH (primary metric)
          splhCurrent
          splhComparison
          splhDeltaPct
          # 2. Sales
          salesCurrent
          salesComparison
          salesDeltaPct
          # 3. Check Count
          checkCountCurrent
          checkCountComparison
          checkCountDeltaPct
          # 4. Labor Hours
          hoursCurrent
          hoursComparison
          hoursDeltaPct
          # 5. Labor Dollars
          laborDollarsCurrent
          laborDollarsComparison
          laborDollarsDeltaPct
          # 6. Labor Percentage
          laborPctCurrent
          laborPctComparison
          laborPctDeltaPct
          # Eligibility
          eligible
          exclusionReason
        }
        bottomPerformers {
          locationId
          locationName
          orgLevel_1_Id
          orgLevel_1_Name
          orgLevel_2_Id
          orgLevel_2_Name
          orgLevel_3_Id
          orgLevel_3_Name
          # 1. SPLH (primary metric)
          splhCurrent
          splhComparison
          splhDeltaPct
          # 2. Sales
          salesCurrent
          salesComparison
          salesDeltaPct
          # 3. Check Count
          checkCountCurrent
          checkCountComparison
          checkCountDeltaPct
          # 4. Labor Hours
          hoursCurrent
          hoursComparison
          hoursDeltaPct
          # 5. Labor Dollars
          laborDollarsCurrent
          laborDollarsComparison
          laborDollarsDeltaPct
          # 6. Labor Percentage
          laborPctCurrent
          laborPctComparison
          laborPctDeltaPct
          # Eligibility
          eligible
          exclusionReason
        }
        byDayOfWeek {
          key
          label
          currentSPLH
          comparisonSPLH
          deltaPct
          currentSales
          comparisonSales
          currentHours
          comparisonHours
        }
        byDaypart {
          key
          label
          currentSPLH
          comparisonSPLH
          deltaPct
          currentSales
          comparisonSales
          currentHours
          comparisonHours
        }
        excludedCount
      }

      # Alerts grouped by metric and severity
      alerts {
        splh {
          critical {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
          warning {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
        }
        sales {
          critical {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
          warning {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
        }
        laborPct {
          critical {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
          warning {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
        }
        hours {
          critical {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
          warning {
            id
            metric
            severity
            scopeLevel
            entityId
            entityName
            parentEntityId
            parentEntityName
            parentLevel
            message
            currentValue
            comparisonValue
            delta
            deltaPct
          }
        }
      }

      # Hierarchy metadata for display
      hierarchyMetadata {
        levels {
          levelId
          label
          order
          isUsed
        }
        clientName
      }

      # Pre-aggregated AI input for parallel call
      aiInput {
        clientId
        comparisonMode
        currentRange {
          startDate
          endDate
        }
        comparisonRange {
          startDate
          endDate
        }
        kpiSummary
        alertsSummary
        topPerformers
        bottomPerformers
      }
    }
  }
`;

/**
 * Query to generate AI insights from pre-aggregated dashboard data
 *
 * @type {string}
 */
export const generateSplhAiInsights = /* GraphQL */ `
  query GenerateSplhAiInsights($input: SPLHAIInput!) {
    generateSplhAiInsights(input: $input) {
      summary
      available
      errorMessage
      generatedAt
    }
  }
`;
